/**
 * JobsWatcher.tsx — app-wide analysis-completion notifier (2026-07-23 UX
 * decision: the user is free to navigate anywhere during the hours-long run;
 * when it finishes, a notification appears wherever they are).
 *
 * Mechanism: polls the org's active jobs of EVERY kind (GET /v1/jobs/active,
 * 15s) — `P-48` widened that endpoint, and this file is why it had to: the
 * completion watcher is the one surface whose whole job is noticing.
 * When a job that was active disappears from the list, it fetches that job's
 * terminal state and shows a dismissible toast — green "Analisi completata"
 * linking to the project's Audit page, or red "Analisi fallita" with the error.
 *
 * Since `P-32` (2026-08-19) the toast does not report what the reconciliation
 * checks concluded; that lives on the Audit page this toast's own button opens.
 * See `completionToast` below and `SHOW_RECONCILIATION_ON_COMPLETION`.
 * Completion also invalidates the tender caches so every visible surface
 * (dashboard rows, stat cards, audit tree) refreshes itself.
 *
 * If the browser was closed when the run finished there is no toast — the
 * results are simply there on the next visit (the Audit page rediscovers the
 * latest job; the dashboard shows the updated project).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useApi } from "../../lib/api";
import { isSelfContainedFailure } from "../../lib/jobFailure";
import {
  categoryReconciliationOf,
  reconciliationOf,
  SHOW_RECONCILIATION_ON_COMPLETION,
  toAnalysisJob,
  useActiveJobs,
  type AnalysisJob,
  type JobDto,
  type JobKind,
} from "../../lib/tenderApi";

/** Five outcomes, not two.
 *
 * `done` vs `ok` is the whole of `P-32` in this file, and after Omar's decision
 * of 2026-08-19 the only thing separating them is the **✓**.
 *
 * Both are green: he asked for the success colour back on the completion
 * surfaces, having been shown that it makes a 76%-over run look like a verified
 * one. That is his call — the product's premise is that the numbers are right,
 * and a permanently hedged completion screen sells doubt on every run.
 *
 * The ✓ did not come back with it, and that is the narrow reading of the
 * instruction rather than an argument with it. This file's own `unknown` entry
 * already establishes that the checkmark is the claim ("No checkmark, no claim
 * about the outcome: we could not read it"), so `ok` — with the tick — stays
 * what a total that was actually CHECKED earns, reachable only with the display
 * gate open. `done` says the run finished. One glyph is a cheap place to keep a
 * true distinction; if Omar wants the tick everywhere too, it is one line.
 *
 * `warn` says an analysis whose totals do not reconcile with the source computo
 * has NOT failed — it ran, it produced rows, and they are wrong; calling that
 * "Analisi non riuscita" is as misleading as calling it done. **`P-32` closed
 * that branch to customers**: it is now reachable only with the display gate
 * open, and the kind is kept — rather than deleted with the display — because
 * Omar's decision was *"for the time being keep the outcome but do not display
 * it"*, and because it is what the control leg of every silence assertion
 * renders.
 *
 * `unknown` exists because the terminal `GET /v1/jobs/{id}` can fail. That path
 * used to emit `ok`, i.e. a green "Analisi completata ✓" — the product asserting
 * success while knowing nothing at all, which is the same rule this file's other
 * comment states and the one place absence still read as a pass. **P-32 does not
 * touch it**: "we could not read the outcome" is not a verdict about the
 * numbers, it is the admission that none was read. */
type ToastKind = "ok" | "done" | "warn" | "error" | "unknown";

interface Toast {
  id: string;
  projectId: string;
  kind: ToastKind;
  /** WHICH job finished (`P-48`). `/v1/jobs/active` returns every kind, so the
   *  title and the button's destination both depend on this — before it existed
   *  a finished ZIP import announced «Analisi completata» and offered to open a
   *  page of audit results that no analysis had produced. */
  jobKind: JobKind;
  text: string;
}

const TOAST_COLOUR: Record<ToastKind, string> = {
  ok: "var(--c-ok)",
  // Green, like `ok` — Omar's 2026-08-19 decision. What separates the two is
  // the ✓ in the title, not the colour.
  done: "var(--c-ok)",
  warn: "var(--c-warn)",
  error: "var(--c-err)",
  unknown: "var(--c-ink-3)",
};

const TOAST_TITLE: Record<ToastKind, string> = {
  ok: "Analisi completata ✓",
  // Same colour as `ok`, and deliberately no ✓ — see the ToastKind note.
  done: "Analisi completata",
  warn: "Analisi completata — totali da verificare",
  error: "Analisi non riuscita",
  // No checkmark, no claim about the outcome: we could not read it.
  unknown: "Analisi terminata — esito non letto",
};

/** `P-48` — the import's own titles. Only three tones are reachable for an
 *  import: it finished, it failed, or its outcome could not be read. There is no
 *  verdict to withhold, so no `ok`/`warn` split. */
const IMPORT_TOAST_TITLE: Record<ToastKind, string> = {
  ok: "Documenti importati ✓",
  done: "Documenti importati",
  warn: "Documenti importati — da verificare",
  error: "Importazione non riuscita",
  unknown: "Importazione terminata — esito non letto",
};

/**
 * The toast's heading, from the tone AND what kind of job it is about.
 *
 * Exported and pure so both tables are reachable from a test without the
 * two-render dance the watcher's effect needs. The lookup used to be a bare
 * `TOAST_TITLE[t.kind]` at the render site, which was correct while every job
 * was an analysis and became a confident lie the moment a second kind existed.
 */
export function toastTitle(kind: ToastKind, jobKind: JobKind): string {
  return jobKind === "import" ? IMPORT_TOAST_TITLE[kind] : TOAST_TITLE[kind];
}

/**
 * What a terminal job is ANNOUNCED as — pure, so both policy legs are testable
 * without the two-render dance the effect needs.
 *
 * `showVerdict` is the `P-32` gate (Omar, 2026-08-19): by default a completed
 * run is announced as completed and nothing is claimed about its totals, which
 * are shown in full by the two Audit banners the toast's own button leads to.
 * Pass `true` and the amber branches below return — the leg that makes "the
 * toast never goes amber" a measured claim instead of an untested silence.
 *
 * What P-32 did NOT touch, and must not:
 *   - `error` — a job that FAILED still says so. That is not a verdict about
 *     numbers, it is the absence of numbers.
 *   - `unknown` — the terminal `GET /v1/jobs/{id}` can fail, and then we know
 *     the run ended and nothing else. Announcing "completata ✓" there would be
 *     the product asserting an outcome nobody read, which is a different rule
 *     entirely and one Omar did not revisit.
 */
/** Identical on both branches on purpose: the TONE and the ✓ carry the
 * difference between "it finished" and "its totals were verified", so the body
 * says the same neutral thing either way. */
const READY = "Analisi completata — i risultati sono pronti.";

export function completionToast(
  job: AnalysisJob,
  showVerdict: boolean = SHOW_RECONCILIATION_ON_COMPLETION,
): { kind: ToastKind; text: string } {
  const completed = job.status === "completed";
  /* `P-48` — an import is answered here and returns BEFORE any of the verdict
     logic below, which is about an analysis that has not happened. Everything
     under this branch reads `job.result.reconciliation`; an import's `result`
     has no such key, so the verdict helpers would return null and the job would
     fall through to «Analisi completata» — success copy for the wrong event,
     and the kind of quiet wrongness that survives a green suite. */
  if (job.kind === "import") {
    if (!completed) {
      return {
        kind: "error",
        // The server's own sentence, which already names the remedy («ricarica
        // lo ZIP per riprovare»). Ours only frames it.
        text: job.error ?? "L'importazione dello ZIP non è riuscita.",
      };
    }
    return {
      kind: "done",
      text: "I documenti dello ZIP sono stati importati e riconosciuti.",
    };
  }
  if (!completed) {
    /* Goal 55B, item 3b — a run the server reports as NOT SAVED already says everything in
       its own sentence: which items were missing, that the previous result is unchanged,
       what to do. «Analisi fallita: Analisi non salvata: …» would say it twice with two
       different verbs, so that sentence is shown as it is — and so is a run STOPPED on a
       Step 1 limit («Analisi fermata …»). The rule is shared with every other surface that
       reports a failed run (`lib/jobFailure.ts`). */
    if (isSelfContainedFailure(job.error)) return { kind: "error", text: job.error };
    return { kind: "error", text: `Analisi fallita${job.error ? `: ${job.error}` : "."}` };
  }
  if (showVerdict) {
    // A completed job is not the same as a trustworthy one. The backend checks
    // every analysis against the total the CME states about itself; when the
    // extraction holds MORE money than the document declares, the totals on
    // screen are wrong.
    //
    // The breakdown gets the same treatment, and needs it independently: on
    // TORINO the grand total reconciles to the cent while € 63.371,00 sits
    // under the wrong sub-category, so `over_extracted` is false and a green
    // toast would still be asserting more than is known.
    const verdict = reconciliationOf(job);
    const categories = categoryReconciliationOf(job);
    if (verdict?.status === "over_extracted") {
      return {
        kind: "warn",
        text:
          "Analisi completata, ma i totali non quadrano con il computo: " +
          "verificali prima di usarli.",
      };
    }
    if (categories?.status === "mismatch") {
      return {
        kind: "warn",
        text:
          "Analisi completata, ma la ripartizione per categoria non coincide " +
          "con quella dichiarata dal computo: verificala prima di usarla.",
      };
    }
    // Nothing disagreed AND we are allowed to say so: this is the one branch
    // that earns the ✓.
    return { kind: "ok", text: READY };
  }
  // `done`, not `ok`. `ok` asserts the totals were CHECKED and agreed. Falling
  // through to it with the gate closed printed a green checkmark over the
  // € 6.782.691,41-vs-€ 3.841.565,84 run, on the same product whose Audit
  // banner paints that job red. The card beside this got hand-written
  // claim-free copy in the same commit; the toast fell through to the
  // pre-existing success string and did not.
  return { kind: "done", text: READY };
}

export function JobsWatcher(): JSX.Element | null {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const activeQuery = useActiveJobs();
  const known = useRef<Map<string, AnalysisJob>>(new Map());
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const current = activeQuery.data;
    if (!current) return;
    const currentIds = new Set(current.map((j) => j.id));

    // Jobs we saw active before that are no longer active → reached a terminal
    // state; fetch it and announce.
    for (const [id, prev] of known.current) {
      if (currentIds.has(id)) continue;
      known.current.delete(id);
      void (async () => {
        try {
          const dto = await api.cmeinsight.get<JobDto>(`/v1/jobs/${id}`);
          const job = toAnalysisJob(dto);
          if (job.status === "completed") {
            void qc.invalidateQueries({ queryKey: ["tender"] });
          }
          const { kind, text } = completionToast(job);
          setToasts((t) => [...t, { id, projectId: job.projectId, kind, jobKind: job.kind, text }]);
        } catch {
          // The terminal fetch failed, so we know the job ENDED and nothing
          // else — not whether it succeeded, not whether its totals reconcile.
          // This branch used to emit `ok`, which rendered "Analisi completata ✓"
          // in green over an analysis that may have failed or over-extracted.
          setToasts((t) => [
            ...t,
            {
              id,
              projectId: prev.projectId,
              kind: "unknown",
              /* From the job we ALREADY saw active, not from the fetch that just
                 failed. That is the only source available here, and it is the
                 right one: the kind cannot change between polls. */
              jobKind: prev.kind,
              text: "Non è stato possibile leggere l'esito: apri il progetto per verificarlo.",
            },
          ]);
        }
      })();
    }
    for (const j of current) known.current.set(j.id, j);
  }, [activeQuery.data, api, qc]);

  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxWidth: 380,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="card"
          style={{
            padding: "12px 14px",
            borderLeft: `3px solid ${TOAST_COLOUR[t.kind]}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ flex: 1, fontSize: 12.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{toastTitle(t.kind, t.jobKind)}</div>
            <div style={{ color: "var(--c-ink-3)" }}>{t.text}</div>
            <button
              className="btn btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => {
                setToasts((x) => x.filter((y) => y.id !== t.id));
                /* `P-48` — an import produces DOCUMENTS, not audit results.
                   Sending the user to `/audit` after one would land them on a
                   page whose banners have nothing to read, because no analysis
                   has run yet. */
                navigate(
                  t.jobKind === "import"
                    ? `/projects/${t.projectId}/documents`
                    : `/audit/${t.projectId}`,
                );
              }}
            >
              {t.jobKind === "import" ? "Apri i documenti" : "Apri il progetto"}
            </button>
          </div>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            aria-label="Chiudi notifica"
            onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
